import React from "react";
import { Link } from "react-router-dom";
import { compose } from "helper/compose";
import { withTranslation, type WithTranslation } from "react-i18next";

import SuperPopin from "../../Common/SuperPopin";
import ResetPasswordForm from "./ResetPasswordForm";
import EmailInformationForm from "./EmailInformationForm";
import { withAuthData, type AuthInjectedProps } from "../../../context/AuthContext";

const SuperPopinAny = SuperPopin as unknown as React.ComponentType<Record<string, unknown>>;
const ResetPasswordFormAny = ResetPasswordForm as unknown as React.ComponentType<
  Record<string, unknown>
>;
const EmailInformationFormAny = EmailInformationForm as unknown as React.ComponentType<
  Record<string, unknown>
>;

interface FormErrorItem {
  code: string;
  message: string;
}

interface ResetPasswordPageOwnProps extends WithTranslation {}

type ResetPasswordPageProps = ResetPasswordPageOwnProps & AuthInjectedProps;

function ResetPasswordPage({
  t,
  actions,
  resetPasswordPageMessage,
  resetPasswordPageEmailMessage,
}: ResetPasswordPageProps) {
  const [formData, setFormData] = React.useState<{ email: string; code: string }>({
    email: "",
    code: "",
  });
  const [displayModal, setDisplayModal] = React.useState(false);
  const [formError, setFormError] = React.useState<FormErrorItem[] | undefined>(undefined);
  const [emailFormError, setEmailFormError] = React.useState<FormErrorItem[] | undefined>(
    undefined
  );

  const switchModal = (display: boolean | null = null) => {
    if (display === null) {
      setDisplayModal((prev) => !prev);
    } else {
      setDisplayModal(!!display);
    }
  };

  const onResetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const field = event.target.name as "email" | "code";
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const onResetSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    actions.removeResetPasswordMessage();
    actions.removeEmailInformationMessage();

    const errors: FormErrorItem[] = [];
    if (formData.email === "") {
      errors.push({ code: "MAIL", message: t("reset.error_reset_submit") });
    }

    setFormError(errors);

    if (errors.length === 0) {
      actions.resetPassword(formData.email);
    }
  };

  const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const field = event.target.name as "email" | "code";
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const onEmailSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    actions.removeResetPasswordMessage();

    const errors: FormErrorItem[] = [];
    if (formData.code === "") {
      errors.push({ code: "CODE", message: t("reset.error_submit_email") });
    }

    setEmailFormError(errors);

    if (errors.length === 0) {
      actions.getEmailInformation(formData.code);
    }
  };

  return (
    <div className="Page AuthPage ResetPasswordPageShell">
      <div className="Page-content AuthPage-content">
        <div className="AuthPage-header">
          <div className="LogoCompanyWrapper">
            <Link to={BASE_PATH + "/"}>
              <div className="LogoCompany isSpaced isGMC"></div>
            </Link>
          </div>
          <p className="AuthPage-kicker">{t("topbar.platform_label")}</p>
          <h2 className="Page-title isSpaced">{t("reset.title")}</h2>
        </div>
        <ResetPasswordFormAny
          onChange={onResetChange}
          onSubmit={onResetSubmit}
          formError={formError}
          message={resetPasswordPageMessage}
          switchModal={switchModal}
        />
        <SuperPopinAny
          shown={displayModal}
          closePopin={() => switchModal(false)}
          additionalClasses={["AuthPopin", "isSmall"]}
        >
          <div className="AuthPopin-card">
            <EmailInformationFormAny
              onChange={onEmailChange}
              onSubmit={onEmailSubmit}
              formError={emailFormError}
              message={resetPasswordPageEmailMessage}
            />
          </div>
        </SuperPopinAny>
      </div>
    </div>
  );
}

export default compose(withTranslation(), withAuthData)(ResetPasswordPage);
